/**
 * Normalizes raw backend UGC video errors into user-friendly messages.
 * Single place for all UGC error mapping to avoid duplicate logic.
 */

const AVATAR_UNAVAILABLE =
  "Ugh-oh! It looks like this avatar isn't available. Please select a different avatar and try again.";

const VOICE_UNAVAILABLE =
  "Ugh-oh! It looks like this voice isn't available. Please select a different voice and try again.";

const VIDEO_SERVICE_BILLING =
  "The video composition service is temporarily unavailable. Please contact support — this isn't your account usage.";

const VIDEO_PROVIDER_BILLING =
  "The avatar video service is temporarily unavailable. Please contact support — this isn't your account usage.";

const GENERIC_ERROR =
  "Oops! It looks like something went wrong. Please try again.";

/**
 * Extracts the backend error message from raw error (JSON object/string or Error).
 */
export function getBackendErrorMessage(raw: unknown): string {
  if (raw == null) return "";
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw) as { errorMessage?: string };
      if (parsed && typeof parsed.errorMessage === "string") return parsed.errorMessage;
    } catch {
      // not JSON, use as-is
    }
    return raw;
  }
  if (typeof raw === "object" && raw !== null && "errorMessage" in raw) {
    const msg = (raw as { errorMessage?: unknown }).errorMessage;
    if (typeof msg === "string") return msg;
  }
  if (raw instanceof Error) return raw.message;
  return String(raw);
}

/**
 * Maps backend error message to a user-friendly string.
 */
export function getFriendlyUGCVideoErrorMessage(backendMessage: string): string {
  const lower = backendMessage.toLowerCase();
  // Avatar not found (HeyGen avatar look not found, look_id, etc.)
  if (
    /avatar\s*(look\s*)?not\s*found|heygen.*avatar|look_id/.test(lower)
  ) {
    return AVATAR_UNAVAILABLE;
  }
  // Voice not found
  if (
    /voice\s*not\s*found|heygen.*voice/.test(lower)
  ) {
    return VOICE_UNAVAILABLE;
  }
  // Provider billing/credit errors — distinct from the user's in-app token usage.
  // Creatomate (subscription usage) vs HeyGen (API credits/balance).
  const isInsufficientCredits = /insufficient\s+credits|subscription\s+usage|out\s+of\s+credits/.test(lower);
  if (isInsufficientCredits) {
    if (/heygen|api[\s_-]?credit|api[\s_-]?balance/.test(lower)) {
      return VIDEO_PROVIDER_BILLING;
    }
    return VIDEO_SERVICE_BILLING;
  }
  // Rate limit, network, timeout, and everything else
  return GENERIC_ERROR;
}

/**
 * Converts any raw UGC video error (from API or catch) to a user-facing message.
 */
export function normalizeUGCVideoError(raw: unknown): string {
  const backendMessage = getBackendErrorMessage(raw);
  if (!backendMessage.trim()) return GENERIC_ERROR;
  return getFriendlyUGCVideoErrorMessage(backendMessage);
}
